/* global globalThis */
import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, updateDoc } from "firebase/firestore";
import { useLocation, useParams } from "react-router-dom";
import { getAuth, signInAnonymously } from "firebase/auth";
import { db } from "../../../../firebase.client.js";

/* --------------------------------------------------
   🔐 AES‑GCM “unhash” helper
--------------------------------------------------*/
// ⚠️ Pull from env in production: VITE_UNSUB_SECRET_KEY
const SECRET_KEY_B64 ="fpOVNyk-oX7m7sUAOvF5CtpUV63hMerZ8vo8hciuliM=";


const b64UrlToUint8Array = (b64url) => {
  const pad = "=".repeat((4 - (b64url.length % 4)) % 4);
  const b64 = (b64url + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw =
    (globalThis.atob ??
      ((str) => Buffer.from(str, "base64").toString("binary")))(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

async function unhashEmail(token) {
  const keyBytes = b64UrlToUint8Array(SECRET_KEY_B64);
  const key = await globalThis.crypto.subtle.importKey(
    "raw",
    keyBytes,
    "AES-GCM",
    false,
    ["decrypt"],
  );

  const blob = b64UrlToUint8Array(token);
  const iv = blob.slice(0, 12);
  const data = blob.slice(12);

  const plainBuf = await globalThis.crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    data,
  );
  return new TextDecoder().decode(plainBuf);
}

/* --------------------------------------------------
   🔍 React-router helper
--------------------------------------------------*/
const useQuery = () => new URLSearchParams(useLocation().search);

/* --------------------------------------------------
   🚪 /unsubscribe/:token component
--------------------------------------------------*/
export default function UnsubscribePage() {
  const { token: tokenParam } = useParams();
  const querySearch = useQuery();
  const [status, setStatus] = useState("pending"); // pending | success | invalid | error

  useEffect(() => {
    let active = true; // avoid setState on unmounted component

    (async () => {
      /* 0️⃣  Ensure we’re signed-in (anonymous) so Firestore rules pass  */
      const auth = getAuth();
      if (!auth.currentUser) {
        try {
          await signInAnonymously(auth);
        } catch (e) {
          console.error("Anonymous sign-in failed", e);
          return active && setStatus("error");
        }
      }

      /* 1️⃣  Extract token from route or ?u= query  */
      const token = tokenParam || querySearch.get("u") || "";
      if (!token) return active && setStatus("invalid");

      try {
        /* 2️⃣  Decrypt → email  */
        const email = await unhashEmail(token);

        /* 3️⃣  Find subscriber  */
        const q = query(collection(db, "users"), where("email", "==", email));
        const snap = await getDocs(q);
        if (snap.empty) return active && setStatus("invalid");

        /* 4️⃣  Flip subscribed → false  */
        await updateDoc(snap.docs[0].ref, {
          subscribed: false,
          unsubscribed: true,
        });

        active && setStatus("success");
      } catch (err) {
        console.error("Unsubscribe error", err);
        active && setStatus("error");
      }
    })();

    return () => {
      active = false;
    };
    // tokenParam / querySearch never change after mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --------------------------------------------------
     🎨 Minimal feedback UI
  --------------------------------------------------*/
  return (
    <main className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
      {status === "pending" && <p>Processing your request…</p>}

      {status === "success" && (
        <>
          <h1 className="text-2xl font-bold mb-2">You’re unsubscribed.</h1>
          <p className="text-gray-600 max-w-md">
            We’re sorry to see you go! Reply to any email if you’d like to
            re-subscribe.
          </p>
        </>
      )}

      {status === "invalid" && (
        <p className="text-red-600">Invalid unsubscribe link. Reach out to admin@tutorspace.app if the problem persists.</p>
      )}

      {status === "error" && (
        <p className="text-red-600">
          Something went wrong. Please try again later. Reach out to admin@tutorspace.app if the problem persists.
        </p>
      )}
    </main>
  );
}

/* --------------------------------------------------
   ⚠️ SECURITY NOTE
--------------------------------------------------
For production, move decrypt + Firestore update into a backend
function. This page can then simply call that endpoint and display
confirmation, removing all Firestore credentials from the client.
--------------------------------------------------*/